/**
 * Cap headroom — how much of a capped reward is left this period (§11.2).
 *
 * Always an estimate, and drawn as one: a dashed track (the provisional
 * language from §2.7), never a solid "progress" bar that reads as fact.
 */

import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type { RuleStatus } from "@/lib/api/client";

export function HeadroomBar({
  status,
  compact = false,
}: {
  status: RuleStatus;
  compact?: boolean;
}) {
  const cap = status.cap_amount ? Number(status.cap_amount) : null;
  const earned = Number(status.earned);
  const used = cap ? Math.min(earned / cap, 1) : 0;
  const full = cap !== null && used >= 1;

  if (status.unavailable) {
    return <p className="text-xs text-faint">{status.unavailable}</p>;
  }

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={cn("truncate", compact ? "text-muted" : "text-ink")}>
          {status.name}
          {status.rate_percent && !status.name.includes(`${trimRate(status.rate_percent)}%`) && (
            <span className="ml-1 text-faint">{trimRate(status.rate_percent)}%</span>
          )}
        </span>
        <span className="shrink-0 tabular-nums text-muted">
          {cap === null ? (
            <>
              {formatMoney(status.earned)} <span className="text-faint">· no cap</span>
            </>
          ) : full ? (
            <span className="text-warn">Cap used</span>
          ) : (
            <>
              {formatMoney(status.headroom)} <span className="text-faint">left</span>
            </>
          )}
        </span>
      </div>
      {cap !== null && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-full border border-dashed border-hairline-strong"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={cap}
          aria-valuenow={Math.min(earned, cap)}
          aria-label={`${status.name}: ${formatMoney(status.earned)} of ${formatMoney(status.cap_amount)} cap used (estimate)`}
        >
          <div
            className={cn("h-full rounded-full", full ? "bg-warn/70" : "bg-brass/70")}
            style={{ width: `${Math.max(used * 100, used > 0 ? 3 : 0)}%` }}
          />
        </div>
      )}
    </div>
  );
}

/** "5.00" → "5", "1.50" → "1.5". */
export function trimRate(rate: string): string {
  return rate.replace(/\.?0+$/, "");
}
